import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
} from "@mui/material";
import { ExitToApp, Notifications, MenuRounded } from "@mui/icons-material";
import { useAppSelector } from "../store/store";
import { useLogoutMutation } from "../features/auth/authApiSlice";
import { selectCurrentUser } from "../store/slices/authSlice";
import Sidebar from "./Sidebar";
import ConfirmationDialog from "./ConfirmationDialog";

interface MainLayoutProps {
  children: React.ReactNode;
  onLogout?: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, onLogout }) => {
  const userDetails = useAppSelector(selectCurrentUser);
  const [logout] = useLogoutMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Get current page name from URL path
  const getCurrentPageName = () => {
    const path = location.pathname.replace("/", "");
    return path || "dashboard";
  };

  // Get formatted page title
  const getPageTitle = () => {
    const pageName = getCurrentPageName();
    return pageName.charAt(0).toUpperCase() + pageName.slice(1);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setShowLogoutConfirmation(true);
  };

  const handleConfirmLogout = async () => {
    try {
      await logout().unwrap();
      onLogout?.();
      // Navigate to login page after successful logout
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirmation(false);
  };

  const sidebarWidth = sidebarOpen ? 240 : 64;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileDrawerOpen}
        onMobileToggle={handleMobileDrawerToggle}
      />{" "}
      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer - 1,
            background:
              "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
            backdropFilter: "blur(20px)",
            color: "white",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 0,
            left: { xs: 0, md: sidebarWidth },
            width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
            transition:
              "left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:
              "0 4px 24px rgba(26, 35, 126, 0.25), 0 2px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 1, sm: 3 } }}>
            {/* Mobile menu button */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2, display: { xs: "block", md: "none" } }}
            >
              <MenuRounded />
            </IconButton>

            {/* Page Title & Breadcrumbs */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "white",
                  letterSpacing: "-0.5px",
                  display: { xs: "none", sm: "block" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                {getPageTitle()}
              </Typography>
              {/* <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                sx={{
                  "& .MuiBreadcrumbs-separator": {
                    color: "text.disabled",
                  },
                  display: { xs: "none", md: "flex" },
                }}
              >
                <Link
                  underline="hover"
                  color="text.secondary"
                  href="/dashboard"
                  sx={{
                    fontSize: "0.875rem",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  Home
                </Link>
                <Typography
                  color="text.primary"
                  sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                >
                  {getPageTitle()}
                </Typography>
              </Breadcrumbs> */}
            </Box>

            {/* Right Side Actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {/* Notifications Icon */}
              <IconButton
                size="medium"
                sx={{
                  display: { xs: "none", sm: "flex" },
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.15)",
                  borderRadius: 2,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(212,175,55,0.2)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(212,175,55,0.3)",
                  },
                }}
              >
                <Notifications />
              </IconButton>

              {/* Settings Icon */}
              {/* <IconButton
                size="medium"
                sx={{
                  display: { xs: "none", sm: "flex" },
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.15)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.25)",
                  },
                }}
              >
                <Settings />
              </IconButton> */}

              {/* User Profile Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  pl: { xs: 1, sm: 2.5 },
                  ml: { xs: 0, sm: 1 },
                  borderLeft: "2px solid",
                  borderColor: "rgba(255,255,255,0.2)",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    textAlign: "right",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    lineHeight={1.3}
                    color="white"
                    sx={{
                      textShadow: "0 1px 4px rgba(0,0,0,0.2)",
                      letterSpacing: "0.2px",
                    }}
                  >
                    {userDetails?.displayName || userDetails?.username}
                  </Typography>
                  <Chip
                    label={userDetails?.role}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      mt: 0.5,
                      bgcolor: "rgba(255,255,255,0.25)",
                      color: "white",
                      fontWeight: 700,
                      border: "1px solid rgba(255,255,255,0.4)",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      backdropFilter: "blur(10px)",
                    }}
                  />
                </Box>
                <IconButton
                  onClick={handleMenu}
                  size="small"
                  sx={{
                    p: 0,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    "&:hover": {
                      transform: "scale(1.08)",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -2,
                      borderRadius: "50%",
                      padding: "2px",
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #d4af37 100%)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: "white",
                      color: "#1a237e",
                      fontWeight: 700,
                      fontSize: "1.125rem",
                      boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {(userDetails?.displayName || userDetails?.username || "U")
                      .charAt(0)
                      .toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  TransitionProps={{
                    timeout: 250,
                  }}
                  PaperProps={{
                    elevation: 12,
                    sx: {
                      mt: 2,
                      borderRadius: 3,
                      boxShadow:
                        "0 12px 48px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
                      minWidth: 220,
                      overflow: "visible",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -8,
                        right: 20,
                        width: 16,
                        height: 16,
                        bgcolor: "background.paper",
                        transform: "rotate(45deg)",
                        boxShadow: "-2px -2px 4px rgba(0,0,0,0.05)",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2.5,
                      py: 2,
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      background:
                        "linear-gradient(135deg, rgba(26, 35, 126, 0.05) 0%, rgba(212, 175, 55, 0.05) 100%)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: "text.primary",
                        mb: 0.5,
                      }}
                    >
                      {userDetails?.displayName || userDetails?.username}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      {userDetails?.username}
                    </Typography>
                  </Box>
                  <MenuItem
                    onClick={handleLogoutClick}
                    sx={{
                      py: 1.5,
                      px: 2.5,
                      gap: 1.5,
                      mt: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(244, 67, 54, 0.08)",
                        "& svg": {
                          color: "error.main",
                        },
                        "& .MuiTypography-root": {
                          color: "error.main",
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ExitToApp
                      fontSize="small"
                      sx={{ transition: "color 0.2s ease" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, transition: "all 0.2s ease" }}
                    >
                      Logout
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            background: "linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)",
            overflowY: "auto",
            overflowX: "hidden",
            pt: { xs: "64px", sm: "72px" },
            pb: 3,
            px: 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {children}
        </Box>
      </Box>
      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        open={showLogoutConfirmation}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access the dashboard."
        type="warning"
        confirmText="Logout"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default MainLayout;
